import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Shield, History, Users, Eye, EyeOff, CheckCircle2, Save } from "lucide-react";

const AccessControlTab = ({
  accessControl,
  availablePages,
  accessLogs,
  savingAccess,
  handleToggleAccess,
  handleSaveAccessControl,
  formatDate,
  getPageLabel,
}) => {
  const roles = ["admin", "manager", "staff"];

  return (
    <>
      <Card className="border-blue-200 bg-blue-50 dark:bg-blue-900/20">
        <CardContent className="p-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-600" />
          <span className="text-sm text-blue-700 dark:text-blue-400">
            Configure which attendance pages are accessible to each role. Staff includes Employee and Team Leader roles.
          </span>
        </CardContent>
      </Card>

      {roles.map((role) => (
        <RoleAccessCard
          key={role}
          role={role}
          accessControl={accessControl}
          availablePages={availablePages}
          savingAccess={savingAccess}
          handleToggleAccess={handleToggleAccess}
          handleSaveAccessControl={handleSaveAccessControl}
        />
      ))}

      <AuditLogCard
        accessLogs={accessLogs}
        formatDate={formatDate}
        getPageLabel={getPageLabel}
      />
    </>
  );
};

const RoleAccessCard = ({
  role,
  accessControl,
  availablePages,
  savingAccess,
  handleToggleAccess,
  handleSaveAccessControl,
}) => {
  const roleLabel = role === "staff" ? "Staff (Employee + Team Leader)" : role;
  const rolePages = accessControl[role] || [];

  return (
    <Card className="border-slate-200 dark:border-slate-700">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="flex items-center gap-2 text-lg capitalize">
          <Users className="w-5 h-5 text-emerald-600" />
          {roleLabel}
        </CardTitle>
        <Button 
          size="sm"
          onClick={() => handleSaveAccessControl(role)}
          disabled={savingAccess}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid={`save-access-${role}`}
        >
          {savingAccess ? "Saving..." : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save
            </>
          )}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {availablePages.map((page) => (
            <PageAccessToggle
              key={page.id}
              page={page}
              hasAccess={rolePages.includes(page.id)}
              role={role}
              handleToggleAccess={handleToggleAccess}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const PageAccessToggle = ({ page, hasAccess, role, handleToggleAccess }) => (
  <div
    className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
      hasAccess
        ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800"
        : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
    }`}
  >
    <div className="flex items-center gap-2">
      {hasAccess ? (
        <Eye className="w-4 h-4 text-emerald-600" />
      ) : (
        <EyeOff className="w-4 h-4 text-slate-400" />
      )}
      <span className={`text-sm font-medium ${hasAccess ? "text-emerald-700 dark:text-emerald-400" : "text-slate-600 dark:text-slate-400"}`}>
        {page.label}
      </span>
    </div>
    <Switch
      checked={hasAccess}
      onCheckedChange={() => handleToggleAccess(role, page.id)}
      data-testid={`toggle-${role}-${page.id}`}
    />
  </div>
);

const AuditLogCard = ({ accessLogs, formatDate, getPageLabel }) => (
  <Card className="border-slate-200 dark:border-slate-700">
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-lg">
        <History className="w-5 h-5 text-emerald-600" />
        Access Control Audit Log
      </CardTitle>
    </CardHeader>
    <CardContent>
      {accessLogs.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No access control changes logged yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date & Time</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Page</TableHead>
                <TableHead>Change</TableHead>
                <TableHead>Updated By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accessLogs.slice(0, 20).map((log) => (
                <AuditLogRow 
                  key={log.log_id} 
                  log={log} 
                  formatDate={formatDate}
                  getPageLabel={getPageLabel}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </CardContent>
  </Card>
);

const AuditLogRow = ({ log, formatDate, getPageLabel }) => (
  <TableRow>
    <TableCell className="text-sm text-slate-500">
      {formatDate(log.updated_at)}
    </TableCell>
    <TableCell className="capitalize font-medium">
      {log.role_affected}
    </TableCell>
    <TableCell>
      {getPageLabel(log.permission_updated)}
    </TableCell>
    <TableCell>
      {log.new_value ? (
        <span className="inline-flex items-center gap-1 text-emerald-600">
          <CheckCircle2 className="w-4 h-4" />
          Enabled
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-red-600">
          <EyeOff className="w-4 h-4" />
          Disabled
        </span>
      )}
    </TableCell>
    <TableCell className="text-sm text-slate-500">
      {log.updated_by_name}
    </TableCell>
  </TableRow>
);

export default AccessControlTab;
